import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { TinyDropDownOption } from '~components/common/TinyDropDown';
import { fetcher } from '~lib/api/fetch';
import {
  COMPANY_SOURCE,
  CompanyEmployee,
  COMPANYS_SOURCE_KEY,
  COMPANYS_SOURCE_VALUE,
  Employee,
  RateAvg,
} from '~types/company';
import { isNumber } from '~utils/common/number';
import { getNumberWithRange, parseToPercent } from '~utils/formatted/number';

import QueryKeys from '../queryKeys';

export type CompanyEmployeeResponse = {
  defaultSource: COMPANYS_SOURCE_KEY;
  employees: {
    [key in COMPANYS_SOURCE_KEY]: CompanyEmployee;
  };
};

const getUrl = (companyHash?: string) => `/api/company/${companyHash}/employee`;

export function useGetCompanyEmplyee(isClosed: boolean, companyHash?: string) {
  const [source, setSource] = useState<TinyDropDownOption[]>([]);

  const { data, status } = useQuery<CompanyEmployeeResponse>(
    [QueryKeys.companyEmployee, companyHash],
    () => fetcher(getUrl(companyHash)),
    { enabled: !isClosed && !!companyHash },
  );

  useEffect(() => {
    if (data) {
      const tempOptions = getAcitveSource(data?.employees);

      const tempSource = Object.values(COMPANY_SOURCE).map((option) => ({
        value: option,
        disabled: tempOptions.indexOf(option) < 0,
        selected: COMPANY_SOURCE[data.defaultSource] === option,
      }));

      setSource(tempSource[0].disabled ? tempSource.slice(1) : tempSource);
    }
  }, [data]);

  return {
    status,
    data,
    defaultSource: data?.defaultSource,
    source,
  };
}

const getAcitveSource = (data: { [key in COMPANYS_SOURCE_KEY]: CompanyEmployee }) => {
  const tempOptions: COMPANYS_SOURCE_VALUE[] = [];
  const keys = Object.keys(data || {}) as COMPANYS_SOURCE_KEY[];
  keys.forEach((source) => COMPANY_SOURCE[source] && tempOptions.push(COMPANY_SOURCE[source]));

  return tempOptions;
};

export const getRankRate = (type: 'hired' | 'left' | 'total', employee: Employee): RateAvg => {
  if (employee.rateType === 'INDUSTRY') {
    return {
      type,
      employee: employee.employee,
      avgRate: getNumberWithRange(employee.industryRate, 0.01, 1),
      totalAvgRate: getNumberWithRange(employee.industryAvgRate, 0, 1),
      rate: employee.rate,
      employeeCount: employee.industryEmployee,
    };
  }

  return {
    type,
    employee: employee.employee,
    avgRate: getNumberWithRange(employee.totalRate, 0.01, 1),
    totalAvgRate: getNumberWithRange(employee.totalAvgRate, 0, 1),
    rate: employee.rate,
    employeeCount: employee.totalEmployee,
  };
};

export const getDisplayPercent = (rate: RateAvg, type: 'hired' | 'left' | 'total') => {
  const percent = parseToPercent(rate.avgRate || 0, 0);

  // 퇴사율이 === 0 일 때, 무조건 1%
  // -> 이유 : 그룹 구간값으로 내려오도록 api 작업되어 있어서 0이 아닌 그룹 구간값이 내려옴(ex 8%). 하지만 0은 무조건 최상위 구간이므로 1% 로 표시
  if (type === 'left' && rate.rate === 0) {
    return 1;
  }

  return percent === 0 ? 1 : percent;
};

export const getRankRateSection = (rate: RateAvg) => {
  if (rate.type === 'total') {
    return {
      main: `${!isNumber(rate.employee) ? '정보없음' : `${rate.employee?.toLocaleString()} 명`}`,
      mainBrace: '',
      description: `상위 ${!isNumber(rate.avgRate) ? ' - ' : getDisplayPercent(rate, rate.type)}% `,
      descriptionBrace: `(평균 : ${
        !isNumber(rate.employeeCount) || rate.employeeCount === null
          ? ' - '
          : Math.round(rate.employeeCount).toLocaleString()
      }명)`,
    };
  }

  return {
    main: `${!isNumber(rate.rate) ? ' - ' : parseToPercent(rate.rate || 0, 0)}%`,
    mainBrace: !isNumber(rate.employee) ? '(정보없음)' : `(${rate.employee?.toLocaleString()} 명)`,
    description: `상위 ${!isNumber(rate.avgRate) ? ' - ' : getDisplayPercent(rate, rate.type)}% `,
    descriptionBrace: `(평균 : ${
      !isNumber(rate.totalAvgRate) ? ' - ' : parseToPercent(rate.totalAvgRate || 0)
    }%)`,
  };
};
